import logger from "@/core/logger"
import settings from "@/core/settings"
import { wrapWithAttribution } from "@/core/parameterlessAttribution"
import { removeInjectedCampaign, resetElements } from "@/placements/injection"
import measurePerformance from "@/core/measurePerformance"
import * as staticProvider from "@/placements/static"
import * as dynamicProvider from "@/placements/dynamic"
export { isFiltered, isFilteringConfigured } from "@/placements/dynamic"
import * as customProvider from "@/placements/custom"
import { AttributedCampaignResult } from "@/types"
import bus from "@/core/api/bus"
import { saveInitialBody } from "./initialBody"
export { initialBody } from "./initialBody"

function getMarkup(campaign: string | AttributedCampaignResult) {
  return (typeof campaign === "string" ? campaign : campaign?.html) ?? ""
}

export function getPlacements() {
  saveInitialBody()
  return [...staticProvider.getPlacements(), ...dynamicProvider.getPlacements()]
}

export async function injectCampaigns(
  campaigns: Record<string, string | AttributedCampaignResult>,
  targets?: Record<string, HTMLElement>
) {
  return measurePerformance("nosto.inject_campaigns", async () => {
    // support for JSON response mode where the content campaigns are wrapped in an object
    const injectableCampaigns: Record<string, string> = {}
    Object.keys(campaigns || {}).forEach(key => {
      injectableCampaigns[key] = getMarkup(campaigns[key])
    })
    const staticPlacements = staticProvider.getPlacementsForInjection()
    const dynamicInjected: [string, HTMLElement[]][] = await dynamicProvider.injectCampaigns(injectableCampaigns)
    dynamicInjected.forEach(([divId]) => {
      if (staticPlacements.includes(divId)) {
        logger.warn(
          `The placement ${divId} is defined as both as static and dynamic placement. In these situations, the dynamic placement takes precedence.`,
          { local: true }
        )
      }
      delete injectableCampaigns[divId]
    })

    const staticInjected: [string, HTMLElement[]][] = await staticProvider.injectCampaigns(injectableCampaigns)
    staticInjected.forEach(res => delete injectableCampaigns[res[0]])

    const customInjected: [string, HTMLElement[]][] = await customProvider.injectCampaigns(injectableCampaigns, targets)
    customInjected.forEach(res => delete injectableCampaigns[res[0]])

    const injection = [...staticInjected, ...dynamicInjected, ...customInjected]
    if (settings.parameterlessAttribution) {
      injection.forEach(([divId, elements]) => {
        elements.forEach(e =>
          wrapWithAttribution(
            e,
            {
              // @ts-expect-error campaign is not a string
              ref: campaigns[divId].result_id
            },
            // @ts-expect-error campaign is not a string
            campaigns[divId].extra_attribution
          )
        )
      })
    }
    const event = {
      responseData: campaigns,
      filledElements: injection.map(e => e[0]),
      unFilledElements: Object.keys(injectableCampaigns)
    }
    bus.emit("postrender", event)
    return {
      filledElements: event.filledElements,
      unFilledElements: event.unFilledElements
    }
  })
}

/**
 * Checks if the placement has filtering configured
 * (only applies for dynamic placements)
 * Use cases: debug toolbar
 * @param {Object}placementConfig
 * @returns {Boolean}
 */
export const removeContent = removeInjectedCampaign

/**
 * Allows to reset the static and dynamic placement's altered content.
 * For dynamic placement, resetting is achieved technically by replacing
 * it with original elements, stored at the moment of campaign injection
 * For static placements, their content will be simply cleared
 * Use cases: debug toolbar preview toggle
 */
export const reset = resetElements
